import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/api';
import { User } from '../../types';

const Login: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersData = await authService.getUsersForLogin();
      setUsers(usersData);
    } catch (error) {
      console.error('ユーザー一覧の取得に失敗しました', error);
      setError('ユーザー一覧の取得に失敗しました');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setPassword('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setError('');
    setLoading(true);

    try {
      const response = await authService.login({
        username: selectedUser.username,
        password
      });
      localStorage.setItem('bizcard_access_token', response.access_token);
      localStorage.setItem('bizcard_refresh_token', response.refresh_token);
      navigate('/cards');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'ログインに失敗しました');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedUser(null);
    setPassword('');
    setError('');
  };

  if (usersLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          読み込み中...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '800px', width: '100%' }}>
        <h2 style={{ textAlign: 'center', marginBottom: '40px', color: '#333', fontSize: '28px' }}>
          名刺管理システム ログイン
        </h2>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            color: '#c33',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '30px',
            border: '1px solid #fcc',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {!selectedUser ? (
          // ユーザー選択画面
          <div>
            <p style={{ textAlign: 'center', marginBottom: '30px', color: '#666', fontSize: '16px' }}>
              ログインするユーザーを選択してください
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {users.map((user) => (
                <div
                  key={user.id}
                  style={{
                    cursor: 'pointer',
                    padding: '20px',
                    border: '2px solid #ddd',
                    borderRadius: '10px',
                    transition: 'all 0.2s ease',
                    textAlign: 'center',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onClick={() => handleUserSelect(user)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#007bff';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,123,255,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#ddd';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                >
                  {/* ユーザー画像 */}
                  <div style={{ marginBottom: '15px' }}>
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.display_name || user.email}
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          margin: '0 auto'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          backgroundColor: '#e9ecef',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#6c757d'
                        }}
                      >
                        {(user.display_name || user.email || user.username || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* ユーザー表示名 */}
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#333' }}>
                    {user.display_name || user.username}
                  </h4>

                  {/* メールアドレス */}
                  <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                    {user.email}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // パスワード入力画面
          <div style={{ maxWidth: '400px', margin: '0 auto' }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              {/* 選択されたユーザー情報 */}
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                {selectedUser.avatar_url ? (
                  <img
                    src={selectedUser.avatar_url}
                    alt={selectedUser.display_name || selectedUser.email}
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      marginBottom: '15px'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: '#e9ecef',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 15px',
                      fontSize: '32px',
                      fontWeight: 'bold',
                      color: '#6c757d'
                    }}
                  >
                    {(selectedUser.display_name || selectedUser.username).charAt(0).toUpperCase()}
                  </div>
                )}
                <h3 style={{ margin: '0 0 5px 0', color: '#333' }}>
                  {selectedUser.display_name || selectedUser.username}
                </h3>
                <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                  {selectedUser.email}
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    パスワード
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {selectedUser?.hint && (
                    <div style={{
                      marginTop: '5px',
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '4px',
                      fontSize: '14px',
                      color: '#6c757d'
                    }}>
                      💡 ヒント: {selectedUser.hint}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={handleBack}
                    style={{
                      flex: '1',
                      padding: '12px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    戻る
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      flex: '2',
                      padding: '12px',
                      backgroundColor: loading ? '#ccc' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'ログイン中...' : 'ログイン'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <Link
            to="/register"
            style={{
              color: '#007bff',
              textDecoration: 'none',
              fontSize: '14px'
            }}
          >
            新規アカウント作成
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;